import { createApi } from '@reduxjs/toolkit/query/react';
import { FindAllNotificationDto, NotificationDto, ResponseCursorPagingDto, ResponseDto } from 'types';
import { NOTIFICATION_TYPE, showNewNotification } from 'utils';
import axiosBaseQuery from 'utils/base-api';
import { offReceiveNotification, onReceiveNotification } from './notifications.socket-client';

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  tagTypes: ['notifications', 'notifications_detail', 'notifications_unread'],
  baseQuery: axiosBaseQuery,
  endpoints: (builder) => ({
    geNotifications: builder.query<ResponseCursorPagingDto<NotificationDto>, FindAllNotificationDto>({
      query: (params) => ({
        url: '/notifications',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        return endpointName + queryArgs.type?.toString();
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.cursor) {
          currentCache.data.rows.push(...newItems.data.rows);
        } else {
          currentCache.data.rows = newItems.data.rows;
        }
        currentCache.data.hasNext = newItems.data.hasNext;
        currentCache.data.nextCursor = newItems.data.nextCursor;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      providesTags: ['notifications']
    }),

    geNotificationsUnreadCount: builder.query<ResponseDto<number>, void>({
      query: () => ({
        url: `/notifications/unread_count`,
        method: 'get'
      }),
      providesTags: ['notifications_unread'],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved }) {
        try {
          await cacheDataLoaded;

          onReceiveNotification((data) => {
            updateCachedData((draft) => {
              draft.data += 1;
            });
            if (data.type === NOTIFICATION_TYPE.INTERNAL_NOTIFICATION) {
              showNewNotification(data);
            }
          });
        } catch (err) {}

        await cacheEntryRemoved;
        offReceiveNotification();
      }
    }),

    readNotification: builder.mutation<ResponseDto<null>, number>({
      query: (notificationId) => ({
        url: `/notifications/read/${notificationId}`,
        method: 'put'
      }),
      invalidatesTags: ['notifications_unread']
    }),

    readAllNotifications: builder.mutation<ResponseDto<null>, void>({
      query: () => ({
        url: `/notifications/read_all`,
        method: 'put'
      }),
      invalidatesTags: ['notifications_unread']
    })
  })
});

export const {
  useGeNotificationsQuery,
  useLazyGeNotificationsQuery,
  useGeNotificationsUnreadCountQuery,
  useReadAllNotificationsMutation,
  useReadNotificationMutation
} = notificationsApi;
